import classNames from "classnames";
import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { ReactComponent as InfoIcon } from "assets/images/common/info.svg";
import { ReactComponent as NotFoundIcon } from "assets/images/common/not-found.svg";
import { ReactComponent as ReloadIcon } from "assets/images/common/reload.svg";

import { BaseOption } from "entities/common";
import { IncidentRow } from "entities/incidents";

import Button from "components/Button";
import DateRangePicker, { DateRangeValue } from "components/DateRangePicker";
import SearchInput from "components/SearchInput";
import Select from "components/Select";
import Table from "components/Table";

import useDebounce from "hooks/useDebounce";
import useRealtime from "hooks/useRealtime";
import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  getIncidentListRequest,
  getIncidentStatisticsRequest,
  resetIncidentList,
  resetIncidentStatistics,
} from "store/slices/incidentsSlice";
import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";
import { formatIncidentTime } from "utils/incidentTime";

import styles from "./style.module.scss";

const PAGE_SIZE = 20;

const STAT_CARDS: { title: string; subtitle: string; key: "total_active" | "active_high" | "resolved_last_30d" }[] = [
  { title: "Total active incidents", subtitle: "Open, all severities", key: "total_active" },
  { title: "Active & high priority", subtitle: "Open, high", key: "active_high" },
  { title: "Resolved", subtitle: "Successfully completed in the last month", key: "resolved_last_30d" },
];

const INCIDENT_TYPE_OPTIONS: BaseOption[] = [
  { value: "", label: "All types" },
  { value: "Air Quality - CO₂", label: "Air Quality - CO₂" },
  { value: "Air Quality - HCHO", label: "Air Quality - HCHO" },
  { value: "Air Quality - TVOC", label: "Air Quality - TVOC" },
  { value: "Air Quality - PM2.5", label: "Air Quality - PM2.5" },
  { value: "Air Quality - PM10", label: "Air Quality - PM10" },
  { value: "Thermal Comfort - Temperature", label: "Thermal Comfort - Temperature" },
  { value: "Thermal Comfort - Humidity", label: "Thermal Comfort - Humidity" },
];

const SEVERITY_OPTIONS: BaseOption[] = [
  { value: "", label: "All severities" },
  { value: "Medium", label: "Medium" },
  { value: "High", label: "High" },
];

const STATUS_OPTIONS: BaseOption[] = [
  { value: "", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "resolved", label: "Resolved" },
  { value: "device_removed", label: "Device removed" },
];

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  resolved: "Resolved",
  device_removed: "Device removed",
};

const HEADERS = [
  { label: "Location", key: "location" },
  { label: "Type", key: "type" },
  { label: "Incident", key: "incident" },
  { label: "Severity", key: "severity" },
  { label: "Time detected", key: "timeDetected" },
  { label: "Duration", key: "duration" },
  { label: "Time resolved", key: "timeResolved" },
  { label: "Status", key: "status" },
];

const IncidentTimeCell: FC<{ iso: string | null; now: number; dash: boolean }> = ({ iso, now, dash }) => {
  if (dash || !iso) {
    return <>-</>;
  }
  const { text, tooltip } = formatIncidentTime(iso, now);
  if (!tooltip) {
    return <span>{text}</span>;
  }
  return (
    <span className={styles.timeCell}>
      {text}
      <span className={styles.timeInfo}>
        <InfoIcon />
        <span className={styles.timeTooltip}>{tooltip}</span>
      </span>
    </span>
  );
};

const AssetIncidentsTab: FC = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { list, meta, statistics, totalIncidents, hasIncidents, listLoaded, isListLoading } = useAppSelector(
    (s: RootStore) => s.incidents,
  );

  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [typeValue, setTypeValue] = useState("");
  const [severityValue, setSeverityValue] = useState("");
  const [statusValue, setStatusValue] = useState("");
  const [dateRange, setDateRange] = useState<DateRangeValue>({ from: null, to: null });
  const [page, setPage] = useState(1);
  const [now, setNow] = useState(() => Date.now());

  const debouncedSearch = useDebounce(search, 500);

  const statisticsQuery = useMemo(
    () => (id ? QueryHelper.getQueryString({ [QUERY_PARAMS.ASSET_ID]: Number(id) }) : ""),
    [id],
  );

  const requestList = useCallback(
    (silent = false) => {
      if (!id) return;
      dispatch(
        getIncidentListRequest(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.ASSET_ID]: Number(id),
            [QUERY_PARAMS.SEARCH]: debouncedSearch.trim() || undefined,
            [QUERY_PARAMS.INCIDENT_TYPE]: typeValue || undefined,
            [QUERY_PARAMS.SEVERITY]: severityValue || undefined,
            [QUERY_PARAMS.STATUS]: statusValue || undefined,
            [QUERY_PARAMS.TIME_DETECTED_FROM]: dateRange.from || undefined,
            [QUERY_PARAMS.TIME_DETECTED_TO]: dateRange.to || undefined,
            [QUERY_PARAMS.PAGE]: page,
            [QUERY_PARAMS.PAGE_SIZE]: PAGE_SIZE,
          }),
          silent ? { silent: true } : undefined,
        ),
      );
    },
    [dispatch, id, debouncedSearch, typeValue, severityValue, statusValue, dateRange.from, dateRange.to, page],
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, typeValue, severityValue, statusValue, dateRange.from, dateRange.to]);

  useEffect(() => {
    requestList();
    dispatch(getIncidentStatisticsRequest(statisticsQuery));
  }, [requestList, dispatch, statisticsQuery]);

  const reload = useCallback(() => {
    requestList(true);
    dispatch(getIncidentStatisticsRequest(statisticsQuery, { silent: true }));
    setNow(Date.now());
  }, [requestList, dispatch, statisticsQuery]);

  useRealtime(["incidents"], reload, { assetId: Number(id) });

  useEffect(() => {
    return () => {
      dispatch(resetIncidentList());
      dispatch(resetIncidentStatistics());
    };
  }, [dispatch]);

  const handleClearFilters = () => {
    setSearch("");
    setTypeValue("");
    setSeverityValue("");
    setStatusValue("");
    setDateRange({ from: null, to: null });
    setPage(1);
  };

  const tableData = useMemo(
    () =>
      list.map((incident: IncidentRow) => {
        const row: Record<string, string | number | ReactElement> = {
          location: <span className={styles.location}>{incident.location}</span>,
          type: <span className={styles.type}>{incident.type}</span>,
          incident: <span className={styles.incident}>{incident.incident}</span>,
          severity: (
            <span className={classNames(styles.severity, styles[`severity-${incident.severity.toLowerCase()}`])}>
              {incident.severity}
            </span>
          ),
          timeDetected: <IncidentTimeCell iso={incident.time_detected} now={now} dash={false} />,
          duration: incident.duration,
          timeResolved: (
            <IncidentTimeCell
              iso={incident.time_resolved}
              now={now}
              dash={incident.status === "active" || incident.status === "device_removed"}
            />
          ),
          status: (
            <span className={classNames(styles.status, styles[`status-${incident.status}`])}>
              {STATUS_LABELS[incident.status]}
            </span>
          ),
        };
        return row;
      }),
    [list, now],
  );

  const totalPages = Math.ceil(meta.total / meta.page_size) || 1;

  return (
    <div className={styles.incidents}>
      {hasIncidents && (
        <div className={styles.stats}>
          {STAT_CARDS.map((card) => (
            <div key={card.title} className={styles.statCard}>
              <span className={styles.statTitle}>{card.title}</span>
              <span className={styles.statValue}>{statistics[card.key]}</span>
              <span className={styles.statSubtitle}>{card.subtitle}</span>
            </div>
          ))}
        </div>
      )}

      <div className={styles.search}>
        <SearchInput
          name="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search incidents..."
          className={styles.searchInput}
        />

        <Button variant="secondary" className={styles.filtersToggle} onClick={() => setShowFilters((prev) => !prev)}>
          {showFilters ? "Close filters" : "Open filters"}
        </Button>
      </div>

      {showFilters && (
        <div className={styles.filters}>
          <div className={styles.filtersWrap}>
            <Select
              variant="secondary"
              options={INCIDENT_TYPE_OPTIONS}
              value={typeValue}
              onChange={setTypeValue}
              placeholder="All types"
              className={styles.filter}
            />

            <Select
              variant="secondary"
              options={SEVERITY_OPTIONS}
              value={severityValue}
              onChange={setSeverityValue}
              placeholder="All severities"
              className={styles.filter}
            />

            <Select
              variant="secondary"
              options={STATUS_OPTIONS}
              value={statusValue}
              onChange={setStatusValue}
              placeholder="All statuses"
              className={styles.filter}
            />

            <DateRangePicker value={dateRange} onChange={setDateRange} className={styles.dateRange} />
          </div>

          <Button form="circle" variant="white" className={styles.reload} onClick={handleClearFilters}>
            <ReloadIcon />
          </Button>

          <Button variant="white" className={styles.clearFilters} onClick={handleClearFilters}>
            Clear filters
          </Button>
        </div>
      )}

      {hasIncidents && (
        <p className={styles.tableTitle}>
          All Incidents <span className={styles.tableTitleCount}>{totalIncidents}</span>
        </p>
      )}

      {listLoaded && !hasIncidents ? (
        <div className={styles.notFound}>
          <NotFoundIcon />
          <p>No incidents yet</p>
        </div>
      ) : listLoaded && list.length === 0 ? (
        <div className={styles.notFound}>
          <NotFoundIcon />
          <p>No incidents found</p>
        </div>
      ) : (
        <div className={styles.tableScroll}>
          <Table
            headers={HEADERS}
            data={tableData}
            pagination={{ total: totalPages, page, onPageChange: setPage }}
            isLoading={isListLoading}
            className={styles.table}
          />
        </div>
      )}
    </div>
  );
};

export default AssetIncidentsTab;
